import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../../componentes/button/button";
import FilialService from "../../../../services/resource/filialService";
import TipoPagamentoService from "../../../../services/resource/tipoPagamentoService";
import NovaSolicitacaoLayout from "../shared/NovaSolicitacaoLayout";
import { useRascunhoFormulario } from "../../Rascunho/RascunhoContext";
import { listaDoRascunho, mesclarComPadrao } from "../../../../utils/solicitacoes/rascunhoStorage";
import SolicitacaoDespesaCampos, { validarCamposDespesa, montarRateioParaEnvio } from "./SolicitacaoDespesaCampos";
import { enviarAnexosStaged } from "../../../../utils/solicitacoes/anexosStaged";
import { enviarComentariosStaged } from "../../../../utils/solicitacoes/comentariosStaged";
import { mapearRateioParaPdf } from "../../../../utils/solicitacoes/orcamento";

const filialService = new FilialService();
const tipoPagamentoService = new TipoPagamentoService();

const inicial = {
    filialId: Number(localStorage.getItem("filialId")) || null,
    fornecedorId: null,
    descricao: "",
    tipoDespesa: "SUPRIMENTOS",
    valorTotal: "",
    vencimento: "",
    tipoPagamento: "",
    chavePix: "",
    rateio: [],
    recorrenciaOcorrenciaId: null,
};

// `initialData` vem de PrefillLancamentoDespesaDTO (deep-link a partir de Despesas Recorrentes) —
// mapeia o rateio (RateioOrcamentarioItemDTO, campo `valorRateado`) para o formato esperado pelo
// RateioOrcamentarioEditor (campo `valor`), e nunca copia número de nota fiscal.
function montarFormInicial(initialData) {
    if (!initialData) return inicial;
    return {
        filialId: initialData.filialId ?? inicial.filialId,
        fornecedorId: initialData.fornecedorId ?? null,
        descricao: initialData.descricao || "",
        tipoDespesa: initialData.tipoDespesa || "SUPRIMENTOS",
        valorTotal: initialData.valorTotal ?? "",
        vencimento: initialData.vencimento || "",
        tipoPagamento: initialData.tipoPagamento || "",
        tipoConta: initialData.tipoConta || "",
        agencia: initialData.agencia || "",
        agenciaDigito: initialData.agenciaDigito || "",
        conta: initialData.conta || "",
        contaDigito: initialData.contaDigito || "",
        chavePix: initialData.chavePix || "",
        rateio: (initialData.rateio || []).map(item => ({ ...item, valor: item.valorRateado, numeroNotaFiscal: "" })),
        recorrenciaOcorrenciaId: initialData.recorrenciaOcorrenciaId ?? null,
    };
}

export default function NovaSolicitacaoDespesaForm({ service, etapaInicialExigeAnexo, onCriada, encaminhar, initialData }) {
    const [form, setForm] = useState(() => montarFormInicial(initialData));
    const [anexos, setAnexos] = useState([]);
    const [comentarios, setComentarios] = useState([]);

    // Rascunho (botão "Salvar rascunho" da página) — só campos, anexos ficam de fora.
    useRascunhoFormulario(
        () => ({ form, comentarios }),
        dados => {
            setForm(mesclarComPadrao(inicial, dados.form));
            setComentarios(listaDoRascunho(dados.comentarios));
        },
    );
    const [filiais, setFiliais] = useState([]);
    const [tiposPagamento, setTiposPagamento] = useState([]);
    const [enviando, setEnviando] = useState(false);
    const [pendentesRecebimento, setPendentesRecebimento] = useState([]);

    const fornecedorInicial = initialData?.fornecedorId
        ? { id: initialData.fornecedorId, nome: initialData.fornecedorNome, cnpj: initialData.fornecedorCnpj }
        : null;

    useEffect(() => {
        filialService.listarAtivas().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
        tipoPagamentoService.listar().then(res => setTiposPagamento(res.data)).catch(() => setTiposPagamento([]));
    }, []);

    function validar() {
        const erro = validarCamposDespesa({ form });
        if (erro) {
            toast.error(erro);
            return false;
        }
        if (etapaInicialExigeAnexo && anexos.length === 0) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;
        if (pendentesRecebimento.length > 0) {
            toast.error("Esta nota deve ser lançada pelo processo de Recebimento de Compras — resolva a compra pendente ou troque o fornecedor.");
            return;
        }

        let usuarioAprovadorId;
        try {
            usuarioAprovadorId = await encaminhar("ETAPA_2_APROVACAO");
        } catch {
            return;
        }

        setEnviando(true);
        service.criarSolicitacao({
            ...form,
            usuarioAprovadorId,
            valorTotal: Number(form.valorTotal),
            rateio: montarRateioParaEnvio(form),
        })
            .then(async res => {
                const [{ falha: falhaAnexos }, { falha: falhaComentarios }] = await Promise.all([
                    enviarAnexosStaged(res.data.id, anexos),
                    enviarComentariosStaged(res.data.id, comentarios),
                ]);
                if (falhaAnexos.length > 0 || falhaComentarios.length > 0) {
                    const partes = [];
                    if (falhaAnexos.length > 0) partes.push(`${falhaAnexos.length} anexo(s) (${falhaAnexos.map(f => `${f.arquivo.name}: ${f.erro}`).join(", ")})`);
                    if (falhaComentarios.length > 0) partes.push(`${falhaComentarios.length} comentário(s)`);
                    toast.error(`Solicitação criada, mas houve falha ao enviar: ${partes.join(" e ")}.`);
                }
                onCriada(res.data);
            })
            .catch(error => console.error("Erro ao criar solicitação", error))
            .finally(() => setEnviando(false));
    }

    return (
        <NovaSolicitacaoLayout
            anexos={anexos}
            onAnexosChange={setAnexos}
            comentarios={comentarios}
            onComentariosChange={setComentarios}
            etapaInicialExigeAnexo={etapaInicialExigeAnexo}
            rateioItens={mapearRateioParaPdf(form.rateio, "valor")}
            rateioItensDetalhados={form.rateio}
            formaPagamento={form}
            sugestoes={["Nota Fiscal", "Boleto", "XML"]}
        >
            <SolicitacaoDespesaCampos
                form={form}
                setForm={setForm}
                filiais={filiais}
                tiposPagamento={tiposPagamento}
                fornecedorInicial={fornecedorInicial}
                onPendenciaRecebimentoChange={setPendentesRecebimento}
            />

            <div className="flex justify-end">
                <Button variant="blue" label="Criar solicitação" iconLeft="fa fa-paper-plane" loading={enviando} disabled={pendentesRecebimento.length > 0} onClick={enviar} />
            </div>
        </NovaSolicitacaoLayout>
    );
}
